import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { cn } from '../../../utils/cn';
import { Icon } from '../Icon/Icon';
import { Spinner } from '../Spinner/Spinner';

export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'dangerText' | 'link';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Icon name or element shown before the label. */
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  'data-autofocus'?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  // Main action on a screen: solid blue.
  primary: 'bg-brand-600 text-white shadow-soft hover:bg-brand-700 active:bg-brand-800',
  secondary: 'bg-navy-800 text-white hover:bg-navy-700 active:bg-navy-900',
  outline: 'border border-line bg-white text-navy-800 shadow-soft hover:border-navy-300 hover:bg-navy-50',
  ghost: 'text-navy-700 hover:bg-navy-100/70 active:bg-navy-100',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
  dangerText: 'text-red-600 hover:bg-red-50 active:bg-red-100',
  link: 'text-brand-600 underline-offset-4 hover:underline px-0 h-auto',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-[13px] gap-1.5 rounded-lg',
  md: 'h-10 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-12 px-6 text-base gap-2 rounded-lg',
};

const ICON_SIZE: Record<ButtonSize, number> = { sm: 16, md: 18, lg: 20 };

const renderIcon = (icon: ReactNode, size: number) => (typeof icon === 'string' ? <Icon name={icon} size={size} /> : icon);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, leftIcon, rightIcon, fullWidth, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-steel focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        SIZES[size],
        VARIANTS[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={ICON_SIZE[size]} /> : leftIcon && renderIcon(leftIcon, ICON_SIZE[size])}
      {children}
      {!loading && rightIcon && renderIcon(rightIcon, ICON_SIZE[size])}
    </button>
  );
});

export default Button;
